// Business › Reports (BL-007 slice 2): the ServiceTitan reports the office runs, each shown to the
// people who hold each report's permission on the server.

import { CHANNEL_WORDS, type Channel } from '../settings/campaigns-model.ts';

export type Report = 'payments' | 'aging' | 'revenue' | 'timesheets' | 'pays' | 'calls' | 'campaigns';

/** Each report and the permission that reads it on the server, whichever role gives it (QA-681). */
export const REPORTS: { report: Report; label: string; lede: string; may: string }[] = [
  {
    report: 'payments',
    label: 'Payments by type',
    lede: 'Payments received each day by how they were paid, with refunds and the net. Voided payments are left out.',
    may: 'reports.view',
  },
  {
    report: 'aging',
    label: 'AR aging',
    lede: 'Open invoices by how long they’ve been owed.',
    may: 'receivables.view',
  },
  {
    report: 'revenue',
    label: 'Revenue by month',
    lede: 'Completed revenue before tax, month by month for 18 months: the invoices issued less the credit notes given, each on its own date.',
    may: 'reports.view', // history fix 1
  },
  {
    report: 'timesheets',
    label: 'Timesheets',
    lede: 'Each technician’s straight time, overtime, double time and time off. Hours only, never pay.',
    may: 'time.correct',
  },
  {
    report: 'pays',
    label: 'Total pays',
    lede: 'What each person is paid for a pay period, as payroll prepared it.',
    may: 'payroll.view',
  },
  {
    report: 'calls',
    label: 'Calls by reason',
    lede: 'Inbound calls by why they did or didn’t book, with the booking rate.',
    may: 'calls.view',
  },
  {
    report: 'campaigns',
    label: 'Campaigns',
    lede: 'Calls, leads, booked work and revenue by campaign, channel or month.',
    may: 'marketing.view',
  },
];

/** reports.campaigns' groupings, as the page offers them. */
export const CAMPAIGN_GROUPS = [
  ['campaign', 'By campaign'],
  ['channel', 'By channel'],
  ['month', 'By month'],
  ['campaign_month', 'Campaign by month'],
] as const;
export type CampaignGroup = (typeof CAMPAIGN_GROUPS)[number][0];

/** The calendar year a date is in, as a range. */
export function yearOf(date: string): [string, string] {
  const y = date.slice(0, 4);
  return [`${y}-01-01`, `${y}-12-31`];
}

/** A 'YYYY-MM' month in words ("October 2026"); a dash for none. */
export const monthWords = (month: string | null) =>
  month
    ? new Intl.DateTimeFormat('en-CA', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
        new Date(`${month}-15T12:00:00Z`),
      )
    : '—';

export const reportsFor = (held: readonly string[]) => REPORTS.filter((r) => held.includes(r.may));

/** Minutes as hours, to two places ("7.50"), as the CSV and ServiceTitan's timesheets read. */
export const hoursText = (minutes: number) => (minutes / 60).toFixed(2);

/** A booking rate (0–1) as a percent to one place; a dash when there's nothing to count. */
export const rateText = (rate: number | null) => (rate === null ? '—' : `${Math.round(rate * 1000) / 10} %`);

/** Return on ad spend (BL-008): revenue for each dollar spent, "3.2×"; "—" with no spend. */
export const roasText = (roas: number | null) => (roas === null ? '—' : `${Math.round(roas * 10) / 10}×`);

/** Hands the browser a CSV the server wrote. */
export function download(file: { fileName: string; csv: string }): void {
  const url = URL.createObjectURL(new Blob([file.csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = file.fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * A campaigns row's first cell: the campaign's name, the channel in words ("Google Ads"), or the month
 * ("October 2026"); what had no campaign says so.
 */
export function campaignRowLabel(
  r: { label: string; channel: string | null; month: string | null },
  groupBy: CampaignGroup,
): string {
  if (groupBy === 'channel')
    return r.channel ? (CHANNEL_WORDS[r.channel as Channel] ?? r.channel) : 'No campaign';
  if (groupBy === 'month') return monthWords(r.month);
  return r.label;
}
